'use client';

import { useState } from 'react';
import { cn } from '../../lib/utils';

export function HtaccessGenerator() {
  const [activeTab, setActiveTab] = useState('redirects');
  const [copied, setCopied] = useState(false);

  // Redirect State
  const [redirectType, setRedirectType] = useState('301');
  const [redirectFrom, setRedirectFrom] = useState('');
  const [redirectTo, setRedirectTo] = useState('');
  const [wwwRedirect, setWwwRedirect] = useState('none');

  // Security State
  const [enableHSTS, setEnableHSTS] = useState(false);
  const [enableXSS, setEnableXSS] = useState(false);
  const [enableClickjacking, setEnableClickjacking] = useState(false);
  const [enableMimeSniffing, setEnableMimeSniffing] = useState(false);

  // Caching State
  const [enableCaching, setEnableCaching] = useState(false);

  // Compression State
  const [enableCompression, setEnableCompression] = useState(false);

  const generateRedirectCode = () => {
    let code = '# Redirects\n';

    // WWW Redirect
    if (wwwRedirect === 'add') {
      code += `RewriteEngine On\nRewriteCond %{HTTP_HOST} !^www\\. [NC]\nRewriteRule ^(.*)$ https://www.%{HTTP_HOST}/$1 [R=301,L]\n\n`;
    } else if (wwwRedirect === 'remove') {
      code += `RewriteEngine On\nRewriteCond %{HTTP_HOST} ^www\\.(.+)$ [NC]\nRewriteRule ^(.*)$ https://%1/$1 [R=301,L]\n\n`;
    }

    // Custom Redirect
    if (redirectFrom && redirectTo) {
      code += `Redirect ${redirectType} ${redirectFrom} ${redirectTo}\n`;
    }

    return code || '# Keine Redirects konfiguriert';
  };

  const generateSecurityCode = () => {
    let code = '# Security Headers\n';
    let hasHeaders = false;

    if (enableHSTS) {
      code += 'Header always set Strict-Transport-Security "max-age=31536000; includeSubDomains; preload"\n';
      hasHeaders = true;
    }

    if (enableXSS) {
      code += 'Header set X-XSS-Protection "1; mode=block"\n';
      hasHeaders = true;
    }

    if (enableClickjacking) {
      code += 'Header always set X-Frame-Options "SAMEORIGIN"\n';
      hasHeaders = true;
    }

    if (enableMimeSniffing) {
      code += 'Header set X-Content-Type-Options "nosniff"\n';
      hasHeaders = true;
    }

    return hasHeaders ? code : '# Keine Security Headers konfiguriert';
  };

  const generateCachingCode = () => {
    if (!enableCaching) return '# Caching nicht aktiviert';

    return `# Browser Caching
<IfModule mod_expires.c>
  ExpiresActive On

  # Images
  ExpiresByType image/jpeg "access plus 1 year"
  ExpiresByType image/jpg "access plus 1 year"
  ExpiresByType image/png "access plus 1 year"
  ExpiresByType image/gif "access plus 1 year"
  ExpiresByType image/webp "access plus 1 year"
  ExpiresByType image/svg+xml "access plus 1 year"
  ExpiresByType image/x-icon "access plus 1 year"

  # CSS, JavaScript
  ExpiresByType text/css "access plus 1 month"
  ExpiresByType text/javascript "access plus 1 month"
  ExpiresByType application/javascript "access plus 1 month"

  # Fonts
  ExpiresByType font/woff2 "access plus 1 year"
  ExpiresByType font/woff "access plus 1 year"
  ExpiresByType font/ttf "access plus 1 year"

  # HTML
  ExpiresByType text/html "access plus 0 seconds"
</IfModule>`;
  };

  const generateCompressionCode = () => {
    if (!enableCompression) return '# Komprimierung nicht aktiviert';

    return `# Gzip Komprimierung
<IfModule mod_deflate.c>
  AddOutputFilterByType DEFLATE text/html
  AddOutputFilterByType DEFLATE text/css
  AddOutputFilterByType DEFLATE text/javascript
  AddOutputFilterByType DEFLATE text/xml
  AddOutputFilterByType DEFLATE text/plain
  AddOutputFilterByType DEFLATE application/javascript
  AddOutputFilterByType DEFLATE application/json
  AddOutputFilterByType DEFLATE application/xml
  AddOutputFilterByType DEFLATE application/xhtml+xml
  AddOutputFilterByType DEFLATE application/rss+xml
  AddOutputFilterByType DEFLATE font/ttf
  AddOutputFilterByType DEFLATE font/woff
  AddOutputFilterByType DEFLATE font/woff2
  AddOutputFilterByType DEFLATE image/svg+xml
</IfModule>`;
  };

  const generateFullCode = () => {
    const sections = [];

    const redirectCode = generateRedirectCode();
    if (redirectCode !== '# Keine Redirects konfiguriert') {
      sections.push(redirectCode);
    }

    const securityCode = generateSecurityCode();
    if (securityCode !== '# Keine Security Headers konfiguriert') {
      sections.push(securityCode);
    }

    const cachingCode = generateCachingCode();
    if (cachingCode !== '# Caching nicht aktiviert') {
      sections.push(cachingCode);
    }

    const compressionCode = generateCompressionCode();
    if (compressionCode !== '# Komprimierung nicht aktiviert') {
      sections.push(compressionCode);
    }

    return sections.length > 0 ? sections.join('\n\n') : '# Keine Konfiguration vorhanden\n# Wähle Optionen aus, um .htaccess Code zu generieren';
  };

  const generatedCode = generateFullCode();

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const tabs = [
    { id: 'redirects', label: 'Redirects' },
    { id: 'security', label: 'Security' },
    { id: 'caching', label: 'Caching' },
    { id: 'compression', label: 'Komprimierung' }
  ];

  return (
    <div className="bg-white rounded-lg shadow-lg p-8">
      <h3 className="font-heading font-bold text-2xl mb-4">
        .htaccess Generator
      </h3>
      <p className="text-gray-700 mb-6">
        Generiere professionelle .htaccess Regeln für Redirects, Security Headers, Caching und Komprimierung.
      </p>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'px-4 py-2 font-semibold border-b-2 transition-colors',
              activeTab === tab.id
                ? 'border-accent text-accent'
                : 'border-transparent text-gray-600 hover:text-accent'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="mb-6">
        {/* Redirects Tab */}
        {activeTab === 'redirects' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-2">
                WWW-Präfix
              </label>
              <select
                value={wwwRedirect}
                onChange={(e) => setWwwRedirect(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
              >
                <option value="none">Keine Änderung</option>
                <option value="add">Zu www. umleiten (example.com → www.example.com)</option>
                <option value="remove">www. entfernen (www.example.com → example.com)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2">
                Redirect Typ
              </label>
              <select
                value={redirectType}
                onChange={(e) => setRedirectType(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
              >
                <option value="301">301 - Permanent</option>
                <option value="302">302 - Temporary</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2">
                Von (Alter Pfad)
              </label>
              <input
                type="text"
                value={redirectFrom}
                onChange={(e) => setRedirectFrom(e.target.value)}
                placeholder="/old-page.html"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2">
                Zu (Neuer Pfad)
              </label>
              <input
                type="text"
                value={redirectTo}
                onChange={(e) => setRedirectTo(e.target.value)}
                placeholder="/new-page.html"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="space-y-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={enableHSTS}
                onChange={(e) => setEnableHSTS(e.target.checked)}
                className="mt-1 w-5 h-5 text-accent"
              />
              <div>
                <div className="font-semibold">HSTS (HTTP Strict Transport Security)</div>
                <div className="text-sm text-gray-600">Erzwingt HTTPS-Verbindungen</div>
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={enableXSS}
                onChange={(e) => setEnableXSS(e.target.checked)}
                className="mt-1 w-5 h-5 text-accent"
              />
              <div>
                <div className="font-semibold">XSS Protection</div>
                <div className="text-sm text-gray-600">Schutz vor Cross-Site-Scripting Angriffen</div>
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={enableClickjacking}
                onChange={(e) => setEnableClickjacking(e.target.checked)}
                className="mt-1 w-5 h-5 text-accent"
              />
              <div>
                <div className="font-semibold">Clickjacking Protection (X-Frame-Options)</div>
                <div className="text-sm text-gray-600">Verhindert Einbettung in iFrames</div>
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={enableMimeSniffing}
                onChange={(e) => setEnableMimeSniffing(e.target.checked)}
                className="mt-1 w-5 h-5 text-accent"
              />
              <div>
                <div className="font-semibold">MIME-Sniffing Protection</div>
                <div className="text-sm text-gray-600">Verhindert MIME-Type Sniffing</div>
              </div>
            </label>
          </div>
        )}

        {/* Caching Tab */}
        {activeTab === 'caching' && (
          <div className="space-y-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={enableCaching}
                onChange={(e) => setEnableCaching(e.target.checked)}
                className="mt-1 w-5 h-5 text-accent"
              />
              <div>
                <div className="font-semibold">Browser Caching aktivieren</div>
                <div className="text-sm text-gray-600">
                  Optimiert Ladezeiten durch Browser-Caching von Bildern, CSS, JS und Fonts
                </div>
              </div>
            </label>

            {enableCaching && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>ℹ️ Cache-Zeiten:</strong>
                </p>
                <ul className="text-sm text-blue-800 mt-2 space-y-1">
                  <li>• Bilder, Fonts: 1 Jahr</li>
                  <li>• CSS, JavaScript: 1 Monat</li>
                  <li>• HTML: Kein Caching</li>
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Compression Tab */}
        {activeTab === 'compression' && (
          <div className="space-y-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={enableCompression}
                onChange={(e) => setEnableCompression(e.target.checked)}
                className="mt-1 w-5 h-5 text-accent"
              />
              <div>
                <div className="font-semibold">Gzip Komprimierung aktivieren</div>
                <div className="text-sm text-gray-600">
                  Reduziert Dateigröße von HTML, CSS, JS und weiteren Textformaten um bis zu 70%
                </div>
              </div>
            </label>

            {enableCompression && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800">
                  <strong>✓ Komprimierte Dateitypen:</strong>
                </p>
                <ul className="text-sm text-green-800 mt-2 space-y-1">
                  <li>• HTML, CSS, JavaScript</li>
                  <li>• XML, JSON</li>
                  <li>• SVG, Fonts (TTF, WOFF, WOFF2)</li>
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Generated Code */}
      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-semibold">
              Generierter .htaccess Code
            </label>
            <button
              onClick={copyToClipboard}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-semibold transition-colors',
                copied
                  ? 'bg-green-500 text-white'
                  : 'bg-accent hover:bg-accent-dark text-white'
              )}
            >
              {copied ? '✓ Kopiert!' : 'Kopieren'}
            </button>
          </div>
          <div className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto">
            <pre className="text-sm font-mono whitespace-pre-wrap">
              {generatedCode}
            </pre>
          </div>
        </div>

        {/* Warning */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            <strong>⚠️ Wichtig:</strong> Teste die .htaccess Datei immer erst auf einer Entwicklungsumgebung.
            Fehlerhafte Konfigurationen können deine Website unzugänglich machen.
          </p>
        </div>

        {/* Usage Instructions */}
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="font-semibold text-sm mb-2">So verwendest du den Code:</p>
          <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1">
            <li>Kopiere den generierten Code</li>
            <li>Erstelle eine Datei namens ".htaccess" (mit Punkt am Anfang)</li>
            <li>Füge den Code in die Datei ein</li>
            <li>Lade die Datei in dein Root-Verzeichnis hoch</li>
            <li>Teste deine Website gründlich</li>
          </ol>
        </div>
      </div>
    </div>
  );
}
